import React from "react";
import { Link } from "react-router-dom";
import { catalogEntry } from "../../config/catalog";
import type { CatalogId } from "../../config/catalog";
import { SALES_EMAIL } from "../../config/links";
import { useProductEntitlements } from "../../hooks/useProductEntitlements";
import NavBar from "./NavBar";
import Spinner from "./Spinner";

/**
 * Guards the pages of a product or service. While the plan is loading it waits; when the plan
 * cannot be loaded it lets the page open (fail-open, as the cards do; the hook logs the error);
 * when the account definitely does not include the product it says so, with a way to ask.
 */
const EntitledRoute: React.FC<{ product: CatalogId; children: React.ReactNode }> = ({ product, children }) => {
  const { loading, isEntryLocked } = useProductEntitlements();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper-200">
        <Spinner />
      </div>
    );
  }
  if (!isEntryLocked(product)) return <>{children}</>;

  const { title } = catalogEntry(product);
  const subject = encodeURIComponent(`Adding ${title} to our Telcofy plan`);
  return (
    <div className="min-h-screen">
      <NavBar title={title} showBackButton backTo="/" />
      <main className="mx-auto max-w-xl px-6 py-16">
        <h1 className="font-display text-3xl font-bold text-ink mb-3">Not included in your plan</h1>
        <p className="text-ink-muted mb-6">{`${title} isn't part of your plan, so this page isn't available to you.`}</p>
        <div className="flex flex-wrap items-center gap-4 text-sm font-semibold">
          <a href={`mailto:${SALES_EMAIL}?subject=${subject}`} className="text-brand-600 hover:underline">
            {`Contact ${SALES_EMAIL}`}
          </a>
          <Link to="/" className="text-ink-muted hover:underline">
            Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default EntitledRoute;
